import React, { useState, useEffect } from "react";
import { Box, Typography, Card } from "@mui/material";
import {
  startOfWeek,
  addDays,
  format,
  isSameDay,
} from "date-fns";

/**
 * Group reservations by date (YYYY-MM-DD)
 */
const groupByDate = (reservations) => {
  return reservations.reduce((acc, res) => {
    const key = format(new Date(res.start_time), "yyyy-MM-dd");
    if (!acc[key]) acc[key] = [];
    acc[key].push(res);
    return acc;
  }, {});
};

function WeeksMobile({ reservationList, selectedDate }) {
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const [selectedDay, setSelectedDay] = useState(selectedDate);

  useEffect(() => {
    setSelectedDay(selectedDate);
  }, [selectedDate]);

  const daysOfWeek = Array.from({ length: 7 }).map((_, i) =>
    addDays(weekStart, i)
  );

  const grouped = groupByDate(reservationList);
  const selectedKey = format(selectedDay, "yyyy-MM-dd");
  const dayReservations = grouped[selectedKey] || [];

  return (
    <Box sx={{ px: 2 }}>
      {/* ===== DAY SELECTOR ===== */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        {daysOfWeek.map((day) => {
          const isSelected = isSameDay(day, selectedDay);

          return (
            <Box
              key={day.toString()}
              onClick={() => setSelectedDay(day)}
              sx={{
                minWidth: 38,
                textAlign: "center",
                px: 1,
                py: 0.75,
                borderRadius: "10px",
                backgroundColor: isSelected ? "#E6F2FF" : "transparent",
                cursor: "pointer",
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#4A5D73",
                }}
              >
                {format(day, "EEEEE")}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#4A5D73",
                }}
              >
                {format(day, "dd")}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ===== DATE HEADER ===== */}
      <Typography
        sx={{
          fontWeight: 600,
          color: "#5A6B8C",
          mb: 1.5,
        }}
      >
        {format(selectedDay, "EEEE, MMMM dd")}
      </Typography>

      {/* ===== AGENDA CARDS ===== */}
      {dayReservations.length === 0 ? (
        <Typography
          sx={{
            color: "#9AA4B2",
            fontSize: 14,
            mt: 2,
          }}
        >
          No reservations
        </Typography>
      ) : (
        dayReservations.map((res, index) => {
          const start = new Date(res.start_time);
          const end = new Date(res.end_time);

          const totalMinutes = Math.max(
            0,
            Math.floor((end - start) / (1000 * 60))
          );
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;

          // USE BACKEND FIELD
          const statusText = res.approved ?? "Pending";

          return (
            <Card
              key={index}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: "14px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              {/* Machine name */}
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                {res.name}
              </Typography>

              {/* Time range */}
              <Typography
                variant="body2"
                sx={{ color: "#5A6B8C", mb: 0.25 }}
              >
                {format(start, "HH:mm")} - {format(end, "HH:mm")}
              </Typography>

              {/* Duration */}
              <Typography
                variant="body2"
                sx={{ color: "#5A6B8C", mb: 0.25 }}
              >
                {hours} hr {minutes} mins
              </Typography>

              {/* Approval status */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  mb: 0.25,
                  color:
                    statusText === "Approved"
                      ? "#2E7D32"
                      : statusText === "Rejected"
                      ? "#C62828"
                      : "#ED6C02",
                }}
              >
                Approval Status: {statusText}
              </Typography>

              {/* User */}
              <Typography variant="body2">
                User: {res.reserver}
              </Typography>
            </Card>
          );
        })
      )}
    </Box>
  );
}

export default WeeksMobile;
